import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { EventCalendar } from '@/types/Calendar.ts'
import moment, { Moment } from 'moment-timezone'

interface UseAgendaScrollParams {
    agendaEvents: EventCalendar[]
    groupedEvents: { [key: string]: EventCalendar[] }
    selectedDate: Moment
    hiddenDays: Date[]
    eventListRef: RefObject<HTMLDivElement | null>
    eventRefs: RefObject<{ [key: string]: HTMLDivElement | null }>
    setSelectedDate: (date: Moment) => void
    setSelectedDateForOtherView: (date: Moment) => void
    hideDaysWithDate: (date: Moment) => void
}

export function useAgendaScroll({
    agendaEvents,
    groupedEvents,
    selectedDate,
    hiddenDays,
    eventListRef,
    eventRefs,
    setSelectedDate,
    setSelectedDateForOtherView,
    hideDaysWithDate
}: UseAgendaScrollParams): void {
    const [scrolling, setScrolling] = useState(false)
    const [firstMonthRender, setFirstRender] = useState(true)

    const scrollingRef: React.MutableRefObject<boolean> = useRef(scrolling)
    scrollingRef.current = scrolling

    const scrollToEventGroup: (formattedDate: string) => void = useCallback((formattedDate: string): void => {
        if (eventRefs.current[formattedDate]?.scrollIntoView) {
            setScrolling(true)
            eventRefs.current[formattedDate]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            setTimeout(() => setScrolling(false), 500)
        }
    }, [])

    function getDateOfNextEvent(date: Moment): Moment {
        const futureEvents: EventCalendar[] = agendaEvents.filter((e: EventCalendar) =>
            moment(e.start).isAfter(date, 'day')
        )
        const previousEvents: EventCalendar[] = agendaEvents.filter((e: EventCalendar) =>
            moment(e.start).isBefore(date, 'day')
        )

        if (futureEvents.length > 0) {
            return moment(
                futureEvents.reduce((acc: EventCalendar, e: EventCalendar) =>
                    moment(e.start).isBefore(acc.start) ? e : acc
                ).start
            )
        }
        if (previousEvents.length > 0) {
            return moment(
                previousEvents.reduce((acc: EventCalendar, e: EventCalendar) =>
                    moment(e.start).isAfter(acc.start) ? e : acc
                ).start
            )
        }
        return date
    }

    // Scroll au premier rendu du mois
    useEffect(() => {
        if (firstMonthRender && eventRefs?.current && Object.keys(eventRefs.current).length > 0) {
            setFirstRender(false)
            const hasEventOnSelectedDate: boolean = !!agendaEvents.find((e: EventCalendar) =>
                moment(e.start).isSame(selectedDate, 'day')
            )
            scrollToEventGroup(
                hasEventOnSelectedDate
                    ? selectedDate.format('YYYY-MM-DD')
                    : getDateOfNextEvent(selectedDate).format('YYYY-MM-DD')
            )
        }
    }, [groupedEvents, eventRefs])

    // Scroll synchronisé avec groupedEvents
    useEffect(() => {
        scrollToEventGroup(selectedDate.format('YYYY-MM-DD'))
    }, [groupedEvents, eventRefs])

    // Scroll listener → met à jour la date sélectionnée
    useEffect(() => {
        const eventListElement: HTMLDivElement | null = eventListRef.current
        if (!eventListElement) return

        const handleScroll: () => void = (): void => {
            if (scrollingRef.current) return

            const containerRect: DOMRect = eventListElement.getBoundingClientRect()
            const eventEntries: [string, HTMLDivElement | null][] = Object.entries(eventRefs.current)

            const mostVisible: [string, HTMLDivElement] | null = getMostVisibleElement(eventEntries, containerRect)
            if (!mostVisible) return

            const [date] = mostVisible
            const dateHidden: boolean = hiddenDays.some(
                (hiddenDate: Date) => moment(hiddenDate).format('YYYY-MM-DD') === date
            )
            if (dateHidden) hideDaysWithDate(moment(date))

            const momentDate: Moment = moment(date, 'YYYY-MM-DD')
            setSelectedDate(momentDate)
            setSelectedDateForOtherView(momentDate)
        }

        eventListElement.addEventListener('scroll', handleScroll)
        return (): void => eventListElement.removeEventListener('scroll', handleScroll)
    }, [hiddenDays])
}

function getMostVisibleElement(
    eventEntries: [string, HTMLDivElement | null][],
    containerRect: DOMRect
): [string, HTMLDivElement] | null {
    let mostVisible: [string, HTMLDivElement] | null = null
    let maxVisibleArea: number = 0

    for (const [date, element] of eventEntries) {
        if (!element) continue
        const elementRect: DOMRect = element.getBoundingClientRect()
        const visibleTop: number = Math.max(elementRect.top, containerRect.top)
        const visibleBottom: number = Math.min(elementRect.bottom, containerRect.bottom)
        const visibleArea: number = Math.max(0, visibleBottom - visibleTop)
        if (visibleArea > maxVisibleArea) {
            maxVisibleArea = visibleArea
            mostVisible = [date, element]
        }
    }

    return mostVisible
}
